import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirm } from '@/components/ConfirmDialog';
import { NAV_MARKER, fallbackPage, historyMarker, isNavigationPage, navigationScope, pageFingerprint } from '@/lib/pageNavigation';

const PageNavigationContext = createContext(null);

export const PageNavigationProvider = ({ children }) => {
  const location = useLocation(), navigate = useNavigate(), action = useNavigationType();
  const { user, loading } = useAuth();
  const confirm = useConfirm();
  const previous = useRef(null), activePages = useRef(new Set()), lock = useRef(false), timer = useRef(null);
  const [marker, setMarker] = useState(null), [pending, setPending] = useState(false);
  const [, refresh] = useState(0);
  const scope = navigationScope(location.pathname, user), view = pageFingerprint(location);
  const notify = useCallback(() => refresh(value => value + 1), []);
  const unlock = useCallback(() => { clearTimeout(timer.current); lock.current = false; setPending(false); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);

  useLayoutEffect(() => {
    if (loading) return;
    const state = window.history.state || {};
    const index = Number.isInteger(state.idx) ? state.idx : 0;
    const next = historyMarker({ scope, index, view, action, previous: previous.current, saved: state[NAV_MARKER] });
    // Preserve React Router's idx/key/usr and all existing caller state.
    window.history.replaceState({ ...state, [NAV_MARKER]: next }, '');
    previous.current = { scope, index, view, marker: next, eligible: isNavigationPage(location.pathname, scope) };
    setMarker(next); unlock();
  }, [location.key, location.pathname, scope, view, action, loading, unlock]);

  const register = useCallback((key, ref) => {
    const registration = { key, ref };
    activePages.current.add(registration); notify();
    return () => { activePages.current.delete(registration); notify(); };
  }, [notify]);
  const pageOptions = () => {
    const configs = [...activePages.current].filter(page => page.key === location.key).map(page => page.ref.current);
    return { ...Object.assign({}, ...configs), dirty: configs.some(config => config.dirty),
      busy: configs.some(config => config.busy), message: configs.find(config => config.dirty && config.message)?.message };
  };
  const options = pageOptions();
  const target = options.fallback || fallbackPage(location, user);
  const hasPrevious = marker?.scope === scope && marker?.view === view && marker.trail.length > 0;
  const canReturn = hasPrevious || (target && target !== `${location.pathname}${location.search}${location.hash}`);

  const goBack = async (explicitFallback) => {
    if (lock.current) return false;
    const config = pageOptions();
    if (config.busy) { toast.info('Wacht tot de huidige bewerking klaar is.'); return false; }
    const state = window.history.state || {}, startKey = state.key;
    const current = state[NAV_MARKER];
    const entry = current?.scope === scope && current?.view === view && Array.isArray(current?.trail) ? current.trail.at(-1) : null;
    const fallback = explicitFallback || config.fallback || fallbackPage(location, user);
    const safeFallback = typeof fallback === 'string' && fallback.startsWith('/') && !fallback.startsWith('//') && !fallback.includes('\\');
    const delta = entry && Number.isInteger(entry.index) && entry.index >= 0 && Number.isInteger(state.idx) ? entry.index - state.idx : 0;
    if (!(delta < 0) && (!safeFallback || fallback === `${location.pathname}${location.search}${location.hash}`)) return false;
    lock.current = true; setPending(true);
    try {
      if (config.dirty && !await confirm({ title: 'Wijzigingen niet opgeslagen', tone: 'warning',
        message: config.message || 'Je hebt niet-opgeslagen wijzigingen. Wil je teruggaan zonder op te slaan?',
        confirmText: 'Terug zonder opslaan', cancelText: 'Blijven' })) { unlock(); return false; }
      if (pageOptions().busy) { unlock(); toast.info('Wacht tot de huidige bewerking klaar is.'); return false; }
      if (window.history.state?.key !== startKey || window.history.state?.[NAV_MARKER]?.scope !== scope) { unlock(); return false; }
      if (delta < 0) navigate(delta);
      else navigate(fallback, { replace: true });
      timer.current = setTimeout(unlock, 1500);
      return true;
    } catch { unlock(); toast.error('Teruggaan is niet gelukt. Probeer opnieuw.'); return false; }
  };

  return <PageNavigationContext.Provider value={{ goBack, disabled: !canReturn || pending || !!options.busy,
    hasPrevious, pending, register, notify }}>{children}</PageNavigationContext.Provider>;
};

export const usePageNavigation = () => useContext(PageNavigationContext);

export const usePageBackOptions = (options) => {
  const { key } = useLocation();
  const context = usePageNavigation();
  const ref = useRef(options); ref.current = options;
  const register = context?.register, notify = context?.notify;
  useLayoutEffect(() => register?.(key, ref), [key, register]);
  useEffect(() => { notify?.(); }, [options.busy, options.fallback, notify]);
  return context?.goBack;
};